import { useState, useEffect } from 'react';
import Product from '@models/product';
import StoresAPI from '@api/stores-api';

interface UseSearchResult {
  searchQuery: string;
  setSearchQuery: (query: string) => void;
  searchResults: Product[];
  setSearchResults: (results: Product[]) => void;
  loading: boolean;
  setLoading: (loading: boolean) => void;
  error: string | null;
  setError: (error: string | null) => void;
  triggerEmptySearch: () => Promise<void>;
}

export const useSearch = (): UseSearchResult => {
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<Product[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const triggerEmptySearch = async () => {
    try {
      setLoading(true);
      setError(null);
      const storesAPI = new StoresAPI();
      const result = await storesAPI.searchProducts('', 1);
      setSearchResults(result.items);
    } catch (err) {
      setError('Failed to fetch Best Discounts on oneplay store');
      console.error('Search error:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!searchQuery.trim()) {
      triggerEmptySearch();
      return;
    }

    const fetchSearchResults = async () => {
      try {
        setLoading(true);
        setError(null);
        const storesAPI = new StoresAPI();
        const result = await storesAPI.searchProducts(searchQuery.trim(), 1);
        setSearchResults(result.items);
      } catch (err) {
        setError('Failed to fetch search results');
        console.error('Search error:', err);
      } finally {
        setLoading(false);
      }
    };

    const debounceTimer = setTimeout(fetchSearchResults, 300);
    return () => clearTimeout(debounceTimer);
  }, [searchQuery]);

  return {
    searchQuery,
    setSearchQuery,
    searchResults,
    setSearchResults,
    loading,
    setLoading,
    error,
    setError,
    triggerEmptySearch
  };
}; 